/**
 * Large File Upload Service - GCS Signed URL Direct Upload
 *
 * Uploads files directly to Google Cloud Storage, bypassing our server.
 * Supports files up to 5TB with progress tracking and abort capability.
 *
 * Flow:
 *  1. Call initiate() to get signed URLs from our backend
 *  2. Upload each file directly to GCS via PUT to the signed URL
 *  3. Call complete() to finalize the submission
 */

import { dumpApi } from './brainDump';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface FileUploadInit {
  filename: string;
  content_type: string;
  size_bytes: number;
}

interface InitiateRequest {
  files: FileUploadInit[];
  text_content: string | null;
  salesperson_id: string | null;
}

interface FileUploadInitResponse {
  filename: string;
  upload_url: string;
  gcs_object_path: string;
  content_type: string;
}

interface InitiateResponse {
  submission_id: string;
  files: FileUploadInitResponse[];
  expires_at: string;
}

interface CompleteFileInfo {
  filename: string;
  content_type: string;
  size_bytes: number;
  gcs_object_path: string;
}

interface CompleteRequest {
  submission_id: string;
  text_content: string | null;
  files: CompleteFileInfo[];
  salesperson_id: string | null;
}

export interface UploadCompleteResponse {
  submission_id: string;
  status: string;
  message: string;
  source_file_count: number;
  source_text_length: number;
}

export interface FileUploadProgress {
  filename: string;
  loaded: number;
  total: number;
  percent: number;
  status: 'pending' | 'uploading' | 'complete' | 'error';
  error?: string;
}

export interface UploadSession {
  submissionId: string;
  files: FileUploadInitResponse[];
  expiresAt: string;
}

// ---------------------------------------------------------------------------
// Core Functions
// ---------------------------------------------------------------------------

/** Step 1: Get signed URLs from our backend. */
export async function initiateUpload(
  files: File[],
  textContent: string | null,
  salespersonId: string | null = null,
): Promise<UploadSession> {
  const fileInits: FileUploadInit[] = files.map((f) => ({
    filename: f.name,
    content_type: f.type || 'application/octet-stream',
    size_bytes: f.size,
  }));

  const payload: InitiateRequest = {
    files: fileInits,
    text_content: textContent,
    salesperson_id: salespersonId,
  };

  const response = await dumpApi.post<InitiateResponse>(
    '/dump/upload/initiate',
    payload,
    { timeout: 30_000 },
  );

  return {
    submissionId: response.data.submission_id,
    files: response.data.files,
    expiresAt: response.data.expires_at,
  };
}

/**
 * Step 2: Upload a single file directly to GCS via the signed URL.
 *
 * Uses XMLHttpRequest for progress tracking (fetch doesn't support upload progress).
 */
export function uploadFileToGCS(
  file: File,
  signedUrl: string,
  contentType: string,
  onProgress: (loaded: number, total: number) => void,
  abortSignal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.open('PUT', signedUrl, true);
    xhr.setRequestHeader('Content-Type', contentType);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress(event.loaded, event.total);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.statusText}`));
      }
    };

    xhr.onerror = () => {
      reject(new Error('Network error during upload'));
    };

    xhr.ontimeout = () => {
      reject(new Error('Upload timed out'));
    };

    // Handle abort
    if (abortSignal) {
      if (abortSignal.aborted) {
        reject(new Error('Upload aborted'));
        return;
      }
      abortSignal.addEventListener('abort', () => {
        xhr.abort();
        reject(new Error('Upload aborted'));
      });
    }

    xhr.send(file);
  });
}

/** Step 3: Tell the backend all uploads are done. */
export async function completeUpload(
  submissionId: string,
  files: CompleteFileInfo[],
  textContent: string | null,
  salespersonId: string | null = null,
): Promise<UploadCompleteResponse> {
  const payload: CompleteRequest = {
    submission_id: submissionId,
    text_content: textContent,
    files,
    salesperson_id: salespersonId,
  };

  const response = await dumpApi.post<UploadCompleteResponse>(
    '/dump/upload/complete',
    payload,
    { timeout: 30_000 },
  );

  return response.data;
}

// ---------------------------------------------------------------------------
// Orchestrator: Full upload flow
// ---------------------------------------------------------------------------

export interface LargeUploadOptions {
  files: File[];
  textContent: string | null;
  salespersonId?: string | null;
  onFileProgress: (fileIndex: number, progress: FileUploadProgress) => void;
  onOverallProgress: (loaded: number, total: number) => void;
  abortSignal?: AbortSignal;
}

/**
 * Orchestrates the full large-file upload:
 *   initiate -> upload each file to GCS -> complete
 */
export async function uploadLargeFiles(
  options: LargeUploadOptions,
): Promise<UploadCompleteResponse> {
  const { files, textContent, salespersonId, onFileProgress, onOverallProgress, abortSignal } =
    options;

  // Step 1: Initiate
  const session = await initiateUpload(files, textContent, salespersonId ?? null);

  // Step 2: Upload each file to GCS
  const totalBytes = files.reduce((acc, f) => acc + f.size, 0);
  const loadedPerFile = new Array(files.length).fill(0);

  const updateOverall = () => {
    const totalLoaded = loadedPerFile.reduce((a: number, b: number) => a + b, 0);
    onOverallProgress(totalLoaded, totalBytes);
  };

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const signedInfo = session.files[i];

    onFileProgress(i, {
      filename: file.name,
      loaded: 0,
      total: file.size,
      percent: 0,
      status: 'uploading',
    });

    try {
      await uploadFileToGCS(
        file,
        signedInfo.upload_url,
        signedInfo.content_type,
        (loaded, total) => {
          loadedPerFile[i] = loaded;
          updateOverall();
          onFileProgress(i, {
            filename: file.name,
            loaded,
            total,
            percent: total > 0 ? Math.round((loaded / total) * 100) : 0,
            status: 'uploading',
          });
        },
        abortSignal,
      );

      loadedPerFile[i] = file.size;
      updateOverall();
      onFileProgress(i, {
        filename: file.name,
        loaded: file.size,
        total: file.size,
        percent: 100,
        status: 'complete',
      });
    } catch (err) {
      onFileProgress(i, {
        filename: file.name,
        loaded: loadedPerFile[i],
        total: file.size,
        percent: 0,
        status: 'error',
        error: err instanceof Error ? err.message : 'Upload failed',
      });
      throw err;
    }
  }

  // Step 3: Complete
  const completeFiles: CompleteFileInfo[] = files.map((f, i) => ({
    filename: f.name,
    content_type: f.type || 'application/octet-stream',
    size_bytes: f.size,
    gcs_object_path: session.files[i].gcs_object_path,
  }));

  return completeUpload(session.submissionId, completeFiles, textContent, salespersonId ?? null);
}
